import { useEffect, useState, useMemo } from "react";
import { getEvents, startCheckout, submitSongRequest, listPublicSongRequests, upvoteSongRequest, joinWaitlist, validateDiscount, extractApiError } from "@/lib/api";
import { ArrowUpRight, ArrowUp, Calendar, Clock, MapPin, Ticket, Users, Music, Utensils, Gift as GiftIcon, Trophy, Sparkles, Dices, X, Music2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";

const INCLUDES = [
  { icon: Sparkles, text: "Interactive cocktail-making experience" },
  { icon: Ticket, text: "Cocktails made during the class" },
  { icon: Users, text: "Professional instruction from The Social Bar" },
  { icon: Music, text: "DJ Stuntman spinning throughout" },
  { icon: Utensils, text: "Light bites" },
  { icon: Dices, text: "Games" },
  { icon: Trophy, text: "Prizes" },
  { icon: GiftIcon, text: "All cocktail-making supplies" },
];

function useCountdown(targetIso) {
  const target = useMemo(() => new Date(targetIso).getTime(), [targetIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff / 3600000) % 24);
  const m = Math.floor((diff / 60000) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s };
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(null); // {event, tier}
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1, attendees: [""], bestie_partner_email: "", discount_code: "" });
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, discount, subtotal_after }
  const [discountBusy, setDiscountBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [song, setSong] = useState({ song: "", artist: "", requester_name: "", dedication: "" });
  const [songSubmitting, setSongSubmitting] = useState(false);
  const [songSent, setSongSent] = useState(false);
  const [songList, setSongList] = useState([]);
  const [votedIds, setVotedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("ss_voted_songs") || "[]")); }
    catch { return new Set(); }
  });
  const [waitlistForm, setWaitlistForm] = useState({ name: "", email: "", phone: "" });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSent, setWaitlistSent] = useState(false);

  useEffect(() => {
    getEvents().then(setEvents).catch(() => toast.error("Could not load event")).finally(() => setLoading(false));
  }, []);

  // Poll the public song board every 8s while the events page is open
  useEffect(() => {
    if (!events[0]?.id) return;
    const eventId = events[0].id;
    let cancelled = false;
    const refresh = () => {
      listPublicSongRequests(eventId).then((list) => { if (!cancelled) setSongList(list); }).catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [events]);

  const event = events[0];
  const cd = useCountdown(event?.date_iso || "2026-10-11T15:00:00-05:00");

  const openBuy = (ev, tierKey) => {
    const tier = ev.ticket_tiers?.find(t => t.key === tierKey) || ev.ticket_tiers?.[0];
    const initialQty = tier?.min_quantity || 1;
    setBuyOpen({ event: ev, tier });
    setForm({ name: "", email: "", phone: "", quantity: initialQty, attendees: Array(initialQty).fill(""), bestie_partner_email: "", discount_code: "" });
    setAppliedDiscount(null);
  };

  const setQuantity = (n) => {
    const tier = buyOpen.tier;
    const step = tier?.quantity_step || 1;
    const minQ = tier?.min_quantity || 1;
    n = Math.max(minQ, Math.round(n / step) * step);
    setForm(f => ({ ...f, quantity: n, attendees: Array.from({ length: n }, (_, i) => f.attendees[i] || "") }));
    setAppliedDiscount(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!buyOpen) return;
    if (form.attendees.some(a => !a.trim())) { toast.error("Please enter all attendee names."); return; }
    const isBestie = buyOpen.tier.key === "bestie";
    const partnerEmail = form.bestie_partner_email.trim();
    if (isBestie && partnerEmail && partnerEmail.toLowerCase() === form.email.trim().toLowerCase()) {
      toast.error("Bestie email must be different from yours.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        event_id: buyOpen.event.id,
        quantity: Number(form.quantity),
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        origin_url: window.location.origin,
        tier_key: buyOpen.tier.key,
        attendee_names: form.attendees.join(" · "),
      };
      if (isBestie && partnerEmail) {
        payload.bestie_partner_email = partnerEmail;
        payload.bestie_partner_name = form.attendees.length > 1 ? form.attendees[1] : "";
      }
      if (appliedDiscount?.code) payload.discount_code = appliedDiscount.code;
      const res = await startCheckout(payload);
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(extractApiError(err, "Could not start checkout"));
      setSubmitting(false);
    }
  };

  if (loading) return <div data-testid="events-page" className="pt-40 pb-32 mx-auto max-w-[1400px] px-5 md:px-10" style={{ color: "var(--ss-cream-dim)" }}>Loading…</div>;
  if (!event) return <div data-testid="events-page" className="pt-40 pb-32 mx-auto max-w-[1400px] px-5 md:px-10" style={{ color: "var(--ss-cream-dim)" }}>No upcoming events. Check back soon.</div>;

  const generalTier = event.ticket_tiers?.find(t => t.key === "general") || { name: "General Admission", price: 75 };
  const bestieTier = event.ticket_tiers?.find(t => t.key === "bestie");

  const submitSong = async (e) => {
    e.preventDefault();
    if (!song.song.trim()) { toast.error("Add a song title first."); return; }
    setSongSubmitting(true);
    try {
      const res = await submitSongRequest(event.id, {
        event_id: event.id,
        song: song.song.trim(),
        artist: song.artist.trim(),
        requester_name: song.requester_name.trim(),
        dedication: song.dedication.trim(),
      });
      setSongSent(true);
      setSong({ song: "", artist: "", requester_name: "", dedication: "" });
      toast.success("Request in. DJ Stuntman got it.");
      // Refresh the board immediately with the new track pinned in
      listPublicSongRequests(event.id).then(setSongList).catch(() => {});
      return res;
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't send that one."));
    } finally {
      setSongSubmitting(false);
    }
  };

  const upvoteSong = async (reqId) => {
    if (votedIds.has(reqId)) return;
    // Optimistic bump
    setSongList((prev) => prev.map((s) => s.id === reqId ? { ...s, votes: (s.votes || 0) + 1 } : s));
    const nextVoted = new Set(votedIds); nextVoted.add(reqId); setVotedIds(nextVoted);
    try { localStorage.setItem("ss_voted_songs", JSON.stringify([...nextVoted])); } catch {}
    try { await upvoteSongRequest(event.id, reqId); }
    catch { toast.error("Couldn't record that vote."); }
  };

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistForm.name.trim() || !waitlistForm.email.trim()) return;
    setWaitlistSubmitting(true);
    try {
      const res = await joinWaitlist(event.id, {
        event_id: event.id,
        name: waitlistForm.name.trim(),
        email: waitlistForm.email.trim(),
        phone: waitlistForm.phone.trim(),
      });
      setWaitlistSent(true);
      setWaitlistForm({ name: "", email: "", phone: "" });
      toast.success(res.already_on_list ? "You're already on the list — we'll be in touch." : "You're on the waitlist. We'll email you the moment a spot opens.");
    } catch (err) {
      toast.error(extractApiError(err, "Couldn't add you to the list."));
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const soldOut = (event.tickets_sold || 0) >= (event.total_tickets || 0);

  return (
    <div data-testid="events-page">
      {/* HERO */}
      <section className="relative min-h-[95svh] overflow-hidden grain on-dark" data-testid="event-hero">
        <img src={event.image_url} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,14,12,0.55), rgba(15,14,12,0.3) 40%, rgba(15,14,12,0.95))" }} />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 pt-40 pb-16 min-h-[95svh] flex flex-col justify-end">
          <div className="font-label text-xs mb-5" style={{ color: "var(--ss-gold)" }}>Upcoming · Simply Social Event · 21+</div>
          <h1 className="font-display font-light tracking-tighter leading-[0.9] text-[12vw] md:text-[7vw] xl:text-[7rem] max-w-[16ch]">
            {event.headline || event.name}
          </h1>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-body" style={{ color: "var(--ss-cream-dim)" }}>
            <span className="inline-flex items-center gap-2"><Calendar size={16} style={{ color: "var(--ss-rust)" }} /> Sunday, October 11, 2026</span>
            <span className="inline-flex items-center gap-2"><Clock size={16} style={{ color: "var(--ss-rust)" }} /> 3:00 PM – 6:00 PM</span>
            <span className="inline-flex items-center gap-2"><MapPin size={16} style={{ color: "var(--ss-rust)" }} /> {event.location}</span>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 border-2 font-label text-xs" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.15)", color: "#F5F1E8" }} data-testid="age-restriction-badge">
            <span style={{ color: "var(--ss-rust)", fontSize: "1rem" }}>●</span> 21+ ONLY · VALID ID REQUIRED AT THE DOOR
          </div>
          <div className="mt-8 font-label text-xs tracking-[0.3em]" style={{ color: "var(--ss-gold)" }}>
            COCKTAILS · MUSIC · FOOD · GAMES · PRIZES · VIBES
          </div>
        </div>
      </section>

      {/* COUNTDOWN */}
      <section className="border-y on-dark" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid="event-countdown">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10 md:py-14 text-center">
          <div className="font-label text-xs mb-6" style={{ color: "var(--ss-gold)" }}>The Countdown to Cocktails Is On 🍸</div>
          <div className="flex justify-center gap-6 md:gap-14">
            {[{ n: cd.d, l: "Days" }, { n: cd.h, l: "Hours" }, { n: cd.m, l: "Minutes" }, { n: cd.s, l: "Seconds" }].map(({ n, l }) => (
              <div key={l}>
                <div className="font-display text-5xl md:text-7xl leading-none" style={{ color: "var(--ss-rust)" }}>{String(n).padStart(2, "0")}</div>
                <div className="font-label text-[0.65rem] mt-2" style={{ color: "var(--ss-cream-dim)" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESCRIPTION */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>The Vibe</div>
        </div>
        <div className="md:col-span-7 space-y-6">
          <Reveal><p className="font-display text-3xl md:text-5xl leading-tight tracking-tight">This is NOT your average cocktail class.</p></Reveal>
          <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
            Simply Social is turning cocktail making into a whole vibe! Grab your bestie, your boo, or your favorite drinking partner and come learn how to shake, pour, and create cocktails with The Social Bar by Simply Social.
          </p>
          <p className="font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
            We're bringing the drinks, the music, the games and the energy. <strong style={{ color: "var(--ss-cream)" }}>DJ Stuntman</strong> will be on the set keeping the vibes going while we mix cocktails, play games, give away prizes and enjoy light bites. Whether you actually want to learn how to make a good drink or you just came for the vibes, you're going to have a GOOD time.
          </p>
          <div className="grid sm:grid-cols-2 gap-2 pt-2">
            {["🍸 Make your own cocktails", "🎧 Vibe with DJ Stuntman", "🍴 Enjoy light bites", "🎲 Play games", "🏆 Win prizes", "🥂 Sip, laugh & get social"].map(t => (
              <div key={t} className="font-body">{t}</div>
            ))}
          </div>
          <p className="font-display italic text-xl md:text-2xl pt-4" style={{ color: "var(--ss-rust)" }}>
            Good drinks. Good music. Good people. GREAT vibes.
          </p>
          <p className="font-body text-sm" style={{ color: "var(--ss-muted)" }}>No boring bartending lecture over here.</p>
        </div>
      </section>

      {/* INCLUDES */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-20 md:pb-28" data-testid="event-includes">
        <div className="font-label text-xs mb-8" style={{ color: "var(--ss-rust)" }}>What's Included</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {INCLUDES.map(({ icon: Icon, text }) => (
            <div key={text} className="p-5 border flex items-start gap-3" style={{ borderColor: "var(--ss-border)" }}>
              <Icon size={18} style={{ color: "var(--ss-rust)", flexShrink: 0, marginTop: 2 }} />
              <span className="font-body text-sm">{text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* TICKETS */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-20 md:pb-28" data-testid="event-tickets">
        <div className="font-label text-xs mb-2" style={{ color: "var(--ss-rust)" }}>{soldOut ? "Sold Out" : "Tickets"}</div>
        <h2 className="font-display text-4xl md:text-6xl tracking-tight mb-4">{soldOut ? "The class is full." : "Pick your ticket."}</h2>
        <div className="mb-10 inline-flex items-center gap-3 px-4 py-3 border-l-4" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.08)" }} data-testid="tickets-age-warning">
          <span className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>21+ EVENT</span>
          <span className="font-body text-sm">Valid government ID required at the door. Absolutely no one under 21 will be admitted, no exceptions.</span>
        </div>
        {soldOut ? (
          <div className="border p-8 md:p-12 grid md:grid-cols-12 gap-10" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.05)" }} data-testid="waitlist-card">
            <div className="md:col-span-5">
              <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Waitlist</div>
              <h3 className="mt-3 font-display text-3xl md:text-5xl tracking-tight leading-[0.95]">Get first dibs<br/>if a spot opens.</h3>
              <p className="mt-6 font-body text-base leading-[1.7]" style={{ color: "var(--ss-ink-dim, #4a463f)" }}>
                Drop your name + email. If someone releases their ticket, we'll email waitlisters first before opening it back to the public.
              </p>
              <div className="mt-4 font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }} data-testid="waitlist-age-warning">
                21+ ONLY · VALID ID REQUIRED AT THE DOOR
              </div>
            </div>
            <form onSubmit={submitWaitlist} className="md:col-span-7 space-y-4" data-testid="waitlist-form">
              <input required data-testid="waitlist-name" placeholder="Your name" className="editorial-input"
                     value={waitlistForm.name} onChange={(e) => setWaitlistForm({ ...waitlistForm, name: e.target.value })} maxLength={80} />
              <input required type="email" data-testid="waitlist-email" placeholder="Email" className="editorial-input"
                     value={waitlistForm.email} onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })} />
              <input data-testid="waitlist-phone" placeholder="Phone (optional)" className="editorial-input"
                     value={waitlistForm.phone} onChange={(e) => setWaitlistForm({ ...waitlistForm, phone: e.target.value })} maxLength={30} />
              <div className="flex items-center justify-between pt-1">
                {waitlistSent ? (
                  <span className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>✓ You're on the list.</span>
                ) : (
                  <span className="font-label text-[0.65rem]" style={{ color: "var(--ss-muted)" }}>We only email you if a seat opens.</span>
                )}
                <button type="submit" data-testid="waitlist-submit" disabled={waitlistSubmitting} className="btn-primary">
                  {waitlistSubmitting ? "Adding…" : "Join Waitlist"} <ArrowUpRight className="arrow" size={14} />
                </button>
              </div>
            </form>
          </div>
        ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* General */}
          <div className="p-8 border flex flex-col" style={{ borderColor: "var(--ss-border)" }} data-testid="ticket-general">
            <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>General Admission</div>
            <div className="font-display text-6xl mt-2">$75</div>
            <div className="mt-1 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>per person</div>
            <p className="mt-6 font-body text-sm flex-1" style={{ color: "var(--ss-cream-dim)" }}>
              Full access to the class, DJ set, light bites, games, and prizes.
            </p>
            <button data-testid="buy-general-btn" onClick={() => openBuy(event, "general")} className="btn-primary mt-8 w-fit">
              Get My Ticket <ArrowUpRight className="arrow" size={14} />
            </button>
          </div>
          {/* Bestie */}
          <div className="relative p-8 border-2 flex flex-col" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.06)" }} data-testid="ticket-bestie">
            <div className="absolute -top-3 left-8 px-3 py-1 font-label text-[0.65rem]" style={{ background: "var(--ss-rust)", color: "#F5F1E8" }}>
              {bestieTier?.badge || "GRAB YOUR BESTIE & SAVE 🥂"}
            </div>
            <div className="font-label text-xs mt-2" style={{ color: "var(--ss-rust)" }}>The Bestie Discount</div>
            <div className="font-display text-6xl mt-2">$130</div>
            <div className="mt-1 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>for two ($65 per person)</div>
            <p className="mt-6 font-body text-sm flex-1" style={{ color: "var(--ss-cream-dim)" }}>
              Because everything is better with your bestie. Sold in pairs only — bring your favorite drinking partner.
            </p>
            <button data-testid="buy-bestie-btn" onClick={() => openBuy(event, "bestie")} className="btn-primary mt-8 w-fit">
              Grab The Bestie Deal <ArrowUpRight className="arrow" size={14} />
            </button>
          </div>
        </div>
        )}
      </section>

      {/* SONG REQUEST */}
      <section className="border-t on-dark" style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }} data-testid="song-request">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Request a Song</div>
            <h2 className="mt-3 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Tell DJ Stuntman<br/>what to spin.
            </h2>
            <p className="mt-6 font-body text-lg leading-[1.75]" style={{ color: "var(--ss-cream-dim)" }}>
              Drop a track (or a whole vibe). We'll keep the list on rotation during the class — no promises on the deep cuts, but we'll try.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 font-label text-[0.65rem]" style={{ color: "var(--ss-gold)" }}>
              <Music2 size={14} /> ADDED LIVE TO THE SET
            </div>
          </div>
          <form onSubmit={submitSong} className="md:col-span-7 space-y-5" data-testid="song-form">
            <input required data-testid="song-title" placeholder="Song title *" className="editorial-input"
                   value={song.song} onChange={(e) => setSong({ ...song, song: e.target.value })} maxLength={120} />
            <input data-testid="song-artist" placeholder="Artist (optional)" className="editorial-input"
                   value={song.artist} onChange={(e) => setSong({ ...song, artist: e.target.value })} maxLength={120} />
            <input data-testid="song-requester" placeholder="Your name (optional)" className="editorial-input"
                   value={song.requester_name} onChange={(e) => setSong({ ...song, requester_name: e.target.value })} maxLength={80} />
            <textarea data-testid="song-dedication" placeholder="Dedication or shout-out (optional)" rows="3" className="editorial-input resize-none"
                      value={song.dedication} onChange={(e) => setSong({ ...song, dedication: e.target.value })} maxLength={240} />
            <div className="flex items-center justify-between pt-2">
              {songSent ? (
                <span className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>✓ On the list. Send another anytime.</span>
              ) : (
                <span className="font-label text-[0.65rem]" style={{ color: "var(--ss-muted)" }}>All fields except song title are optional.</span>
              )}
              <button type="submit" data-testid="song-submit" disabled={songSubmitting} className="btn-primary">
                {songSubmitting ? "Sending…" : "Send To DJ"} <ArrowUpRight className="arrow" size={14} />
              </button>
            </div>
          </form>

          {/* Live Song Board */}
          <div className="md:col-span-12 mt-6" data-testid="song-board">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="font-label text-xs" style={{ color: "var(--ss-gold)" }}>Now On The List</div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight mt-1">Vote up what you want to hear.</h3>
              </div>
              <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-muted)" }}>
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: "var(--ss-rust)" }} />LIVE · {songList.length}
              </div>
            </div>
            {songList.length === 0 ? (
              <div className="border p-6 font-body text-sm" style={{ borderColor: "var(--ss-border)", color: "var(--ss-cream-dim)" }}>
                No requests yet — be the first to add one above.
              </div>
            ) : (
              <ul className="divide-y border" style={{ borderColor: "var(--ss-border)" }}>
                {songList.slice(0, 12).map((s) => {
                  const voted = votedIds.has(s.id);
                  return (
                    <li key={s.id} className="flex items-center gap-4 p-4 md:p-5 transition-colors hover:bg-white/5" data-testid={`song-board-row-${s.id}`}>
                      <button onClick={() => upvoteSong(s.id)} disabled={voted}
                              data-testid={`song-upvote-${s.id}`}
                              className="flex flex-col items-center justify-center w-14 h-14 border transition-all"
                              style={{
                                borderColor: voted ? "var(--ss-rust)" : "var(--ss-border)",
                                background: voted ? "var(--ss-rust)" : "transparent",
                                color: voted ? "#F5F1E8" : "var(--ss-cream)",
                                cursor: voted ? "default" : "pointer",
                              }}
                              aria-label={voted ? "Voted" : "Upvote"}>
                        <ArrowUp size={14} strokeWidth={2.5} />
                        <span className="font-label text-xs mt-0.5">{s.votes || 0}</span>
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="font-body truncate">
                          <span style={{ color: "var(--ss-cream)" }}>{s.song}</span>
                          {s.artist && <span style={{ color: "var(--ss-cream-dim)" }}> — {s.artist}</span>}
                        </div>
                        <div className="font-label text-[0.6rem] mt-1" style={{ color: "var(--ss-muted)" }}>
                          {s.requester_name ? `Requested by ${s.requester_name}` : "Requested anonymously"}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* EVENT DETAILS */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32" data-testid="event-details">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 border-t pt-10" style={{ borderColor: "var(--ss-border)" }}>
          {[
            { l: "Date", v: "Sunday, October 11, 2026" },
            { l: "Time", v: "3:00 PM – 6:00 PM" },
            { l: "Hosted By", v: "Simply Social / The Social Bar" },
            { l: "Music By", v: "DJ Stuntman" },
            { l: "Location", v: event.location },
            { l: "General Admission", v: "$75 per person" },
            { l: "Bestie Discount", v: "$130 for two ($65 each)" },
          ].map(({ l, v }) => (
            <div key={l}>
              <div className="font-label text-[0.65rem] mb-2" style={{ color: "var(--ss-rust)" }}>{l}</div>
              <div className="font-body">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BUY MODAL */}
      {buyOpen && (
        <div data-testid="buy-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ background: "rgba(0,0,0,0.8)" }} onClick={() => !submitting && setBuyOpen(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                className="w-full max-w-lg p-8 md:p-10 border on-dark max-h-[90vh] overflow-y-auto"
                style={{ background: "var(--ss-bg-2)", borderColor: "var(--ss-border)" }}>
            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>{buyOpen.tier.name}</div>
                <h3 className="mt-1 font-display text-3xl tracking-tight">{buyOpen.event.name}</h3>
                <div className="mt-2 text-sm" style={{ color: "var(--ss-cream-dim)" }}>
                  ${buyOpen.tier.price} {buyOpen.tier.per}
                </div>
              </div>
              <button type="button" data-testid="buy-close" onClick={() => setBuyOpen(null)} className="p-1"><X size={20} /></button>
            </div>
            <div className="space-y-5">
              <input required data-testid="buy-name" placeholder="First & Last Name" className="editorial-input"
                     value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input required type="email" data-testid="buy-email" placeholder="Email" className="editorial-input"
                     value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input required data-testid="buy-phone" placeholder="Phone" className="editorial-input"
                     value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <div>
                <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>
                  Number of tickets {buyOpen.tier.key === "bestie" ? "(sold in pairs of 2)" : ""}
                </label>
                <div className="flex items-center gap-4 mt-2">
                  {buyOpen.tier.key === "bestie" ? (
                    <input required type="number" min="2" step="2" data-testid="buy-quantity" className="editorial-input"
                           value={form.quantity} onChange={(e) => setQuantity(Number(e.target.value) || 2)} />
                  ) : (
                    <input required type="number" min="1" step="1" data-testid="buy-quantity" className="editorial-input"
                           value={form.quantity} onChange={(e) => setQuantity(Number(e.target.value) || 1)} />
                  )}
                </div>
                {buyOpen.tier.key === "bestie" && (
                  <div className="mt-2 font-label text-[0.6rem]" style={{ color: "var(--ss-gold)" }}>
                    Bestie tickets are sold in increments of 2.
                  </div>
                )}
              </div>
              <div>
                <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Attendee names</label>
                <div className="space-y-3 mt-2">
                  {form.attendees.map((a, i) => (
                    <input key={i} required data-testid={`attendee-${i}`} placeholder={`Attendee ${i + 1} name`} className="editorial-input"
                           value={a} onChange={(e) => setForm(f => ({ ...f, attendees: f.attendees.map((v, j) => j === i ? e.target.value : v) }))} />
                  ))}
                </div>
              </div>
              {buyOpen.tier.key === "bestie" && (
                <div className="p-4 border" style={{ borderColor: "var(--ss-rust)", background: "rgba(160,30,42,0.08)" }}>
                  <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }}>
                    Split the tickets — email your bestie their spot (optional)
                  </label>
                  <input type="email" data-testid="bestie-partner-email"
                         placeholder="bestie@email.com"
                         className="editorial-input mt-2"
                         value={form.bestie_partner_email}
                         onChange={(e) => setForm({ ...form, bestie_partner_email: e.target.value })} />
                  <div className="mt-2 font-body text-[0.7rem]" style={{ color: "var(--ss-cream-dim)" }}>
                    We'll send a personalized ticket + gifter mention straight to their inbox after payment.
                  </div>
                </div>
              )}
              <div className="p-4 border" style={{ borderColor: "var(--ss-border)" }}>
                <label className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Promo code</label>
                {appliedDiscount ? (
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="font-body text-sm">
                      <span data-testid="applied-code" style={{ color: "var(--ss-cream)" }}>{appliedDiscount.code}</span>
                      <span style={{ color: "var(--ss-gold)" }}> · −${appliedDiscount.discount.toFixed(2)}</span>
                    </div>
                    <button type="button" data-testid="remove-code"
                            onClick={() => { setAppliedDiscount(null); setForm(f => ({ ...f, discount_code: "" })); }}
                            className="font-label text-[0.6rem]" style={{ color: "var(--ss-rust)" }}>REMOVE</button>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <input data-testid="promo-code-input" placeholder="HOLIDAY" className="editorial-input flex-1"
                           value={form.discount_code}
                           onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })} />
                    <button type="button" data-testid="promo-apply-btn" disabled={discountBusy || !form.discount_code.trim()}
                            onClick={async () => {
                              const sub = buyOpen.tier.price * form.quantity;
                              setDiscountBusy(true);
                              try {
                                const res = await validateDiscount({ code: form.discount_code.trim(), subtotal: sub, context: "events" });
                                setAppliedDiscount({ code: res.code, discount: Number(res.discount) || 0 });
                                toast.success(`${res.code} applied — $${Number(res.discount).toFixed(2)} off`);
                              } catch (err) {
                                toast.error(extractApiError(err, "That code isn't valid."));
                              } finally { setDiscountBusy(false); }
                            }}
                            className="btn-primary">
                      {discountBusy ? "…" : "Apply"}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t" style={{ borderColor: "var(--ss-border)" }}>
                <div>
                  <div className="font-label text-[0.6rem]" style={{ color: "var(--ss-cream-dim)" }}>Total</div>
                  <div className="font-display text-3xl" data-testid="buy-total">
                    ${Math.max(0, buyOpen.tier.price * form.quantity - (appliedDiscount?.discount || 0)).toFixed(0)}
                  </div>
                  {appliedDiscount && (
                    <div className="font-label text-[0.6rem] mt-1" style={{ color: "var(--ss-gold)" }}>
                      was ${(buyOpen.tier.price * form.quantity).toFixed(0)} · {appliedDiscount.code} saved ${appliedDiscount.discount.toFixed(2)}
                    </div>
                  )}
                </div>
                <button type="submit" data-testid="buy-submit" disabled={submitting} className="btn-primary">
                  {submitting ? "Redirecting…" : "Pay Now"} <ArrowUpRight className="arrow" size={14} />
                </button>
              </div>
              <div className="pt-2 font-label text-[0.65rem]" style={{ color: "var(--ss-rust)" }} data-testid="modal-age-warning">
                By purchasing you confirm every attendee is 21+. Valid ID required at the door — no refunds for under-21 turnaways.
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
